import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Switch } from "components/ui/switch";
import { Textarea } from "components/ui/textarea";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { cn } from "lib/utils";
import { myProductsKey, productCreateAction } from "action/product-action";
import { useQueryClient } from "@tanstack/react-query";
import { Megaphone } from "lucide-react";
import { useOpenBoost } from "lib/boost";
import { useUserData } from "store/user-data";
import { categories as defaultCategories } from "helpers/constants";
import React, { useState, useEffect } from "react";
import PageHeader from "components/page-header";
import ReadyTimeField from "components/ready-time-field";
import { useSessionControl } from "hooks/use-session-control";
import { getCategoriesAction, createCategoryAction } from "action/category-action";
import { Plus } from "lucide-react";
import ProductOptionsEditor, {
  type OptionRow,
  serializeOptions,
} from "components/product-options-editor";

const schema = z.object({
  name: z.string().min(1, { message: "Your product name is required." }),
  category: z.string().min(1, { message: "Please select a category" }),
  price: z.number().min(1, { message: "Please enter a price" }),
  description: z.string().min(7, { message: "Please enter a description" }),
  available_after_days: z.number().min(0, { message: "Availability cannot be less than 0" }).optional(),
  featured: z.boolean(),
  todayDeal: z.boolean(),
  thumbnailImage: z
    .instanceof(File, { message: "Please add a Product Image" })
    .refine((file) => file instanceof File, {
      message: "Please add a Product Image",
    }),
});

const AddProductComponent = () => {
  const [searchCategoryTerm, setSearchCategoryTerm] = useState("");
  const [allCategories, setAllCategories] = useState<string[]>(defaultCategories);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const { sessionControl } = useSessionControl();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    const response = await getCategoriesAction();
    if (response.status === 200 && response.body) {
      const customCategoryNames = response.body.map(cat => cat.name);
      // Merge and remove duplicates
      const merged = Array.from(new Set([...defaultCategories, ...customCategoryNames]));
      setAllCategories(merged);
    }
  };

  const handleAddCustomCategory = async () => {
    const name = searchCategoryTerm.trim();
    if (!name) return;

    // Normalization: Title Case
    const normalizedName = name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');

    if (normalizedName.length < 2) {
      toast.error("Category name is too short");
      return;
    }

    if (allCategories.includes(normalizedName)) {
      setValue("category", normalizedName);
      setSearchCategoryTerm("");
      return;
    }

    setIsAddingCategory(true);
    const response = await createCategoryAction(normalizedName);
    setIsAddingCategory(false);

    if (response.status === 201) {
      toast.success("Category added successfully");
      setAllCategories(prev => [...prev, response.body.name]);
      setValue("category", response.body.name);
      setSearchCategoryTerm("");
    } else {
      toast.error(response.error || "Failed to add category");
    }
  };

  // Filter categories based on the search term
  const filteredCategories = allCategories.filter((category) =>
    category.toLowerCase().includes(searchCategoryTerm.toLowerCase())
  );

  const {
    register,
    setValue,
    watch,
    getValues,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    mode: "all",
    defaultValues: {
      name: "",
      category: "",
      price: 0,
      description: "",
      available_after_days: 0,
      featured: false,
      todayDeal: false,
      thumbnailImage: null as File,
    },
  });

  const [options, setOptions] = useState<OptionRow[]>([]);
  const queryClient = useQueryClient();
  const openBoost = useOpenBoost();
  const businessId = useUserData((state) => state.userData?.business_id);

  /** Saves the product; with `boost`, goes straight on to boosting it on Meta Ads. */
  function onSubmit(data: z.infer<typeof schema>, boost = false) {
    const serialized = serializeOptions(options);
    if (serialized.error) {
      toast.error(serialized.error);
      return;
    }
    const toastId = toast.loading("adding product...");

    const formData = new FormData();
    formData.append("name", data.name);
    formData.append("category", data.category);
    formData.append("price", data.price.toString());
    formData.append("description", data.description);
    if (
      typeof data.available_after_days !== "undefined" &&
      data.available_after_days !== null
    ) {
      formData.append(
        "available_after_days",
        String(data.available_after_days)
      );
    }
    formData.append("featured", data.featured.toString());
    formData.append("todayDeal", data.todayDeal.toString());
    formData.append("thumbnailImage", data.thumbnailImage);
    formData.append("options", serialized.json);

    // console.log(data);
    productCreateAction(formData)
      .then((response) => {
        if (response.status === 201) {
          toast.dismiss(toastId);
          toast.success("Product created successfully");
          reset();
          setOptions([]);
          queryClient.invalidateQueries({ queryKey: myProductsKey(businessId) });
          if (boost) openBoost(response.body?.id);
        } else {
          sessionControl(response.status);

          toast.dismiss(toastId);
          toast.error(response.error);
        }
      })
      .catch((err: any) => {
        toast.dismiss(toastId);
        toast.error("Failed to create product, An error occurred");
        console.error(err);
      });
  }

  const saveButtons = (
    <>
      <Button variant="outline" onClick={handleSubmit((data) => onSubmit(data))}>
        Save Product
      </Button>
      <Button onClick={handleSubmit((data) => onSubmit(data, true))}>
        <Megaphone className="mr-2 h-4 w-4" />
        Save & Boost on Meta Ads
      </Button>
    </>
  );

  return (
    <div className="w-full">
      <PageHeader
        title="Add Product"
        actions={saveButtons}
      />
      <div className="grid grid-cols-12 gap-5">
        <div className="col-span-12 space-y-5 lg:col-span-8">
          <Card>
            <CardHeader className="border-b px-6 py-5 mb-6">
              <CardTitle className="text-lg">Product Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/*name*/}
              <div className="flex flex-col gap-2">
                <Label className="" htmlFor="p_Fullname">
                  Product Name
                </Label>
                <Input
                  id="p_Fullname"
                  type="text"
                  placeholder="Name"
                  {...register("name")}
                  className={cn("", {
                    "border-destructive": errors.name,
                  })}
                />
              </div>
              {errors.name && (
                <p className="text-destructive mt-2 text-sm">
                  {errors.name.message}
                </p>
              )}

              {/*category*/}
              <div className="flex flex-col gap-2">
                <Label>Category</Label>
                <Select onValueChange={(e) => setValue("category", e)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Category</SelectLabel>
                      {/* Search Input */}
                      <div className="p-2">
                        <Input
                          ref={searchInputRef}
                          type="text"
                          placeholder="Search Categories"
                          value={searchCategoryTerm}
                          onChange={(e) =>
                            setSearchCategoryTerm(e.target.value)
                          }
                          onPointerDown={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            // Prevent Radix from handling keyboard events (like type-ahead) while typing
                            e.stopPropagation();

                            if (e.key === "Enter" && filteredCategories.length > 0) {
                              e.preventDefault();
                              const matched =
                                filteredCategories.find(
                                  (c) =>
                                    c.toLowerCase() ===
                                    searchCategoryTerm.toLowerCase()
                                ) || filteredCategories[0];
                              setValue("category", matched);
                              setSearchCategoryTerm("");
                              // Force close select by blurring the input
                              searchInputRef.current?.blur();
                            }
                          }}
                          className="w-full p-1 px-2 border rounded text-sm"
                        />
                      </div>
                      {/* Render filtered categories */}
                      {filteredCategories.length > 0 ? (
                        filteredCategories.map((category) => (
                          <SelectItem
                            key={category}
                            value={category}
                          >
                            {category}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="p-2 space-y-2">
                          <div className="text-default-500 text-sm flex justify-center">
                            Can't find what you are looking for?
                          </div>
                          {searchCategoryTerm.trim().length >= 2 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="w-full flex items-center justify-center gap-2"
                              onClick={handleAddCustomCategory}
                              disabled={isAddingCategory}
                            >
                              <Plus className="w-4 h-4" />
                              Add "{searchCategoryTerm}"
                            </Button>
                          )}
                        </div>
                      )}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              {errors.category && (
                <p className="text-destructive mt-2 text-sm">
                  {errors.category.message}
                </p>
              )}

              {/*price*/}
              <div className="flex flex-col gap-2">
                <Label className="" htmlFor="price">
                  Price(₦)
                </Label>
                <Input
                  id="price"
                  type="number"
                  placeholder="Price"
                  className={cn("", {
                    "border-destructive": errors.price,
                  })}
                  onChange={(e) => {
                    setValue("price", Number(e.target.value));
                  }}
                />
              </div>
              {errors.price && (
                <p className="text-destructive mt-2 text-sm">
                  {errors.price.message}
                </p>
              )}

              {/*minimum purchase qty*/}
              {/* <div className="flex flex-col gap-2">
                                <Label className="" htmlFor="qty">
                                    Minimum Purchase Qty
                                </Label>
                            </div> */}

              {/* Available after days moved to Availability section below */}

              {/* <Input id="qty" type="text" placeholder="1" disabled={true}/>
                            </div>

                            {/*product tags*/}
              <div className="flex flex-col gap-2">
                <Label className="" htmlFor="tags">
                  Tags
                </Label>
                <Input
                  disabled={true}
                  id="tags"
                  type="text"
                  placeholder="Type & hit enter to add a tag"
                />
              </div>

              {/*barcode*/}
              <div className="flex flex-col gap-2">
                <Label className="" htmlFor="brcode">
                  Barcode
                </Label>
                <Input
                  disabled={true}
                  id="brcode"
                  type="text"
                  placeholder="Code"
                />
              </div>

              {/*is refundable switch*/}
              <div className="flex items-center justify-between gap-4">
                <Label>Refundable</Label>
                <div className="flex-none">
                  <Switch disabled={true} color="success" />
                </div>
              </div>
            </CardContent>
          </Card>
          {/*product description*/}
          <Card>
            <CardHeader className="border-b px-6 py-5 mb-6">
              <CardTitle className="text-lg">Options</CardTitle>
            </CardHeader>
            <CardContent>
              <ProductOptionsEditor value={options} onChange={setOptions} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="border-b px-6 py-5 mb-6">
              <CardTitle className="text-lg">Product Description</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              <Label>Description</Label>
              <div className="flex-1">
                <Textarea
                  id="pn4"
                  placeholder="Product description..."
                  {...register("description")}
                  className={cn("", {
                    "border-destructive": errors.description,
                  })}
                />
                {errors.description && (
                  <p className="text-destructive mt-2 text-sm">
                    {errors.description.message}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
          {/*product images*/}
          <Card>
            <CardHeader className="border-b px-6 py-5 mb-6">
              <CardTitle className="text-lg">Product Images</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/*gallery images*/}
              {/*<div className="flex items-center space-x-6 rtl:space-x-reverse">*/}
              {/*    <Label className="inline-flex text-sm font-normal ">*/}
              {/*        Gallery Images (600 X 600)*/}
              {/*    </Label>*/}
              {/*    <div className="flex-1">*/}
              {/*        <Input*/}
              {/*            disabled={true}*/}
              {/*            className="read-only:leading-0 py-1.5 h-full ps-0"*/}
              {/*            type="file"*/}
              {/*        />*/}
              {/*    </div>*/}
              {/*</div>*/}

              {/*thumb images*/}
              <div className="flex flex-col gap-2">
                <Label>
                  Thumb Images (600 X 600)
                </Label>
                <div className="flex-1">
                  <Input
                    className={cn("read-only:leading-0 py-1.5 h-full ps-0", {
                      "border-destructive": errors.thumbnailImage,
                    })}
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files[0]; // Get the selected file
                      if (file) {
                        // console.log(file); // Do something with the file, like uploading or processing it
                        setValue("thumbnailImage", file);
                      }
                    }}
                  />
                  {errors.thumbnailImage && (
                    <p className="text-destructive mt-2 text-sm">
                      {errors.thumbnailImage.message}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Availability */}
          <Card>
            <CardHeader className="border-b px-6 py-5 mb-6">
              <CardTitle className="text-lg">When is it ready?</CardTitle>
            </CardHeader>
            <CardContent>
              <ReadyTimeField
                value={watch("available_after_days") ?? 0}
                onChange={(days) =>
                  setValue("available_after_days", days, { shouldValidate: true })
                }
              />
            </CardContent>
          </Card>

          {/*seo meta tags*/}
          {/* <Card>
            <CardHeader className="border-b px-6 py-5 mb-6">
              <CardTitle className="text-lg">SEO Meta Tags</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex">
                <Label className="w-[130px]">Meta Title</Label>
                <Input
                  disabled={true}
                  id="meta_title"
                  type="text"
                  placeholder="Title"
                />
              </div>

              <div className="flex">
                <Label className="w-[130px]">Description</Label>
                <Textarea
                  disabled={true}
                  id="pn4"
                  placeholder="Write something for your product seo"
                />
              </div>
              <div className="flex items-center ">
                <Label className="flex-none w-[130px] ">Meta Image</Label>
                <Input
                  disabled={true}
                  className="read-only:leading-0 py-1.5 h-full ps-0"
                  type="file"
                />
              </div>
            </CardContent>
          </Card> */}
        </div>
        <div className="col-span-12 space-y-5 lg:col-span-4 lg:sticky lg:top-24 lg:self-start">
          {/*low stock warning quantity*/}
          {/* <Card>
            <CardHeader className="border-b px-6 py-5 mb-6">
              <CardTitle className="text-lg">Low Stock Quantity Warning</CardTitle>
            </CardHeader>
            <CardContent className="flex items-center">
              <Label>Quantity</Label>
              <Input
                disabled={true}
                id="qty_warning"
                type="text"
                placeholder="1"
              />
            </CardContent>
          </Card> */}

          {/*<Card>*/}
          {/*    <CardHeader className="border-b px-6 py-5 mb-6">*/}
          {/*        <CardTitle className="text-lg">Cash On Delivery</CardTitle>*/}
          {/*    </CardHeader>*/}
          {/*    <CardContent className="space-y-4">*/}
          {/*        <div className="flex justify-between">*/}
          {/*            <Label className="text-sm font-normal">Status</Label>*/}
          {/*            <div>*/}
          {/*                <Switch color="success" />*/}
          {/*            </div>*/}
          {/*        </div>*/}
          {/*    </CardContent>*/}
          {/*</Card>*/}
          <Card>
            <CardHeader className="border-b px-6 py-5 mb-6">
              <CardTitle className="text-lg">Featured</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between">
                <Label className="text-sm font-normal">Status</Label>
                <div>
                  <Switch
                    onClick={() => {
                      setValue("featured", !getValues("featured"));
                    }}
                    color="success"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="border-b px-6 py-5 mb-6">
              <CardTitle className="text-lg">Today's Deal</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between">
                <Label className="text-sm font-normal">Status</Label>
                <div>
                  <Switch
                    onClick={() => {
                      setValue("todayDeal", !getValues("todayDeal"));
                    }}
                    color="success"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
          {/*<Card>*/}
          {/*    <CardHeader className="border-b px-6 py-5 mb-6">*/}
          {/*        <CardTitle className="text-lg">Flash Deal</CardTitle>*/}
          {/*    </CardHeader>*/}
          {/*    <CardContent className="space-y-4">*/}
          {/*        <div className="flex items-center">*/}
          {/*            <Label>Discount</Label>*/}
          {/*            <Input id="discount" type="text" placeholder="0"/>*/}
          {/*        </div>*/}

          {/*        <div className="flex items-center">*/}
          {/*            <Label>Type</Label>*/}
          {/*            <Select>*/}
          {/*                <SelectTrigger>*/}
          {/*                    <SelectValue placeholder="Select Type"/>*/}
          {/*                </SelectTrigger>*/}
          {/*                <SelectContent>*/}
          {/*                    <SelectGroup>*/}
          {/*                        <SelectLabel>Type</SelectLabel>*/}
          {/*                        <SelectItem value="apple">Apple</SelectItem>*/}
          {/*                        <SelectItem value="banana">Banana</SelectItem>*/}
          {/*                        <SelectItem value="blueberry">Blueberry</SelectItem>*/}
          {/*                        <SelectItem value="grapes">Grapes</SelectItem>*/}
          {/*                        <SelectItem value="pineapple">Pineapple</SelectItem>*/}
          {/*                    </SelectGroup>*/}
          {/*                </SelectContent>*/}
          {/*            </Select>*/}
          {/*        </div>*/}
          {/*    </CardContent>*/}
          {/*</Card>*/}
        </div>
        <div className="col-span-12 flex flex-wrap justify-end gap-2">{saveButtons}</div>
      </div>
    </div>
  );
};

export default AddProductComponent;
